import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaSave,
  FaPrint,
  FaLock,
  FaRedo,
  FaUpload,
  FaFile,
  FaImage,
  FaFilePdf,
  FaPills,
  FaUser,
  FaComments,
  FaFileInvoice,
  FaFolder,
  FaTimes,
  FaCalendarAlt,
} from "react-icons/fa";
import CustomButton from "../../../shared/CustomButton";
import { useTranslation } from "react-i18next";
const PrescriptionTab = ({ patient }) => {
  const { t } = useTranslation();
  // Sample data
  const [medicines, setMedicines] = useState([
    {
      id: 1,
      name: "Paracetamol",
      dosage: "500mg",
      route: "Oral",
      presentation: "Tablet",
      activeIngredients: "Paracetamol",
      info: "Pain reliever and fever reducer",
      manufacturer: "ABC Pharmaceuticals",
      registrationAuthority: "FDA",
      registrationNumber: "REG12345",
      country: "USA",
    },
    {
      id: 2,
      name: "Amoxicillin",
      dosage: "250mg",
      route: "Oral",
      presentation: "Capsule",
      activeIngredients: "Amoxicillin trihydrate",
      info: "Antibiotic for bacterial infections",
      manufacturer: "XYZ Pharma",
      registrationAuthority: "FDA",
      registrationNumber: "REG67890",
      country: "USA",
    },
  ]);

  const [prescriptions, setPrescriptions] = useState([
    {
      id: 1,
      prescriptionNumber: "PRX123456789ABCDEF",
      date: "2024-06-28",
      diagnosis: "Common Cold",
      physician: "Dr. Smith",
      pharmacy: "City Pharmacy",
      status: "Completed",
      dateCollected: "2024-06-30",
      isFinalized: true,
      medicines: [
        {
          id: 1,
          name: "Paracetamol",
          dosage: "500mg",
          route: "Oral",
          frequency: "Twice daily",
          duration: "5 days",
          purpose: "Fever and pain relief",
          refills: "0",
          billQuantity: 2,
          billPrice: 150.0,
        },
      ],
      comments: [
        {
          id: 1,
          text: "Patient advised to take with food",
          date: "2024-06-28",
          files: [],
        },
      ],
      files: [],
      // Bill-related fields
      billDate: "2024-06-28",
      billNote: "Medicine covered under insurance",
      paymentMethod: "card",
      discountType: "%",
      discountValue: 10,
      insuranceProvider: 15,
    },
  ]);

  const [physicians] = useState([
    { id: 1, name: "Dr. Smith" },
    { id: 2, name: "Dr. Johnson" },
    { id: 3, name: "Dr. Williams" },
  ]);

  const [pharmacies] = useState([
    { id: 1, name: "City Pharmacy" },
    { id: 2, name: "Health Plus Pharmacy" },
    { id: 3, name: "MediCare Pharmacy" },
  ]);

  const statusOptions = [
    "Pending",
    "Sent to Pharmacy",
    "Received by Pharmacy",
    "In Process",
    "Ready For Pickup",
    "On Hold",
    "Cancelled",
    "Rejected",
    "Completed",
    "Refilled",
    "Expired",
    "Renewed",
  ];

  // State
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [currentPrescription, setCurrentPrescription] = useState({
    id: null,
    prescriptionNumber: generatePrescriptionNumber(),
    date: new Date().toISOString().split("T")[0],
    diagnosis: "",
    physician: "",
    pharmacy: "",
    status: "Pending",
    dateCollected: "",
    isFinalized: false,
    medicines: [],
    comments: [],
    files: [],
    // Bill-related fields
    billDate: new Date().toISOString().split("T")[0],
    billNote: "",
    paymentMethod: "cash",
    discountType: t("Rs."),
    discountValue: 0,
    insuranceProvider: 5,
  });

  const [showPrescriptionSearch, setShowPrescriptionSearch] = useState(false);
  const [prescriptionSearchTerm, setPrescriptionSearchTerm] = useState("");
  const [showMedicineSearch, setShowMedicineSearch] = useState(false);
  const [medicineSearchTerm, setMedicineSearchTerm] = useState("");
  const [showMedicineForm, setShowMedicineForm] = useState(false);
  const [activeTab, setActiveTab] = useState("patient");
  const [newComment, setNewComment] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);

  // Refs
  const prescriptionSearchRef = useRef(null);
  const medicineSearchRef = useRef(null);
  const commentFileInputRef = useRef(null);
  const prescriptionFileInputRef = useRef(null);

  const [newMedicine, setNewMedicine] = useState({
    name: "",
    dosage: "",
    route: "",
    presentation: "",
    activeIngredients: "",
    info: "",
    manufacturer: "",
    registrationAuthority: "",
    registrationNumber: "",
    country: "",
  });

  // Helper functions
  function generatePrescriptionNumber() {
    return Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    )
      .join("")
      .toUpperCase();
  }

  const calculatePrescriptionBillTotals = () => {
    const subtotal = currentPrescription.medicines.reduce(
      (sum, medicine) =>
        sum + (medicine.billQuantity || 1) * (medicine.billPrice || 0),
      0
    );

    let discount = 0;
    if ((currentPrescription.discountType || t("Rs.")) === t("Rs.")) {
      discount = currentPrescription.discountValue || 0;
    } else {
      discount = (subtotal * (currentPrescription.discountValue || 0)) / 100;
    }

    const afterDiscount = subtotal - discount;
    const insuranceAmount =
      (afterDiscount * (currentPrescription.insuranceProvider || 5)) / 100;
    const finalAmount = afterDiscount - insuranceAmount;

    return {
      subtotal,
      discount,
      afterDiscount,
      insuranceAmount,
      finalAmount,
    };
  };

  const handleNewPrescription = () => {
    const newPrescription = {
      id: null,
      prescriptionNumber: generatePrescriptionNumber(),
      date: new Date().toISOString().split("T")[0],
      diagnosis: "",
      physician: "",
      pharmacy: "",
      status: "Pending",
      dateCollected: "",
      isFinalized: false,
      medicines: [],
      comments: [],
      files: [],
      // Bill-related fields
      billDate: new Date().toISOString().split("T")[0],
      billNote: "",
      paymentMethod: "cash",
      discountType: "Rs.",
      discountValue: 0,
      insuranceProvider: 5,
    };
    setCurrentPrescription(newPrescription);
    setSelectedPrescription(newPrescription);
    setShowPrescriptionSearch(false);
  };

  const handleSelectPrescription = (prescription) => {
    setSelectedPrescription(prescription);
    if (!prescription.isFinalized) {
      setCurrentPrescription(prescription);
    }
    setShowPrescriptionSearch(false);
  };

  const handleAddMedicine = (medicine) => {
    const medicineToAdd = {
      id: medicine.id,
      name: medicine.name,
      dosage: medicine.dosage,
      route: medicine.route,
      frequency: "",
      duration: "",
      purpose: "",
      refills: "",
      // Bill-related fields
      billQuantity: 1,
      billPrice: 0,
    };

    if (selectedPrescription?.isFinalized) return;

    setCurrentPrescription((prev) => ({
      ...prev,
      medicines: [...prev.medicines, medicineToAdd],
    }));
    setShowMedicineSearch(false);
  };

  const handleMedicineFieldChange = (medicineId, field, value) => {
    if (selectedPrescription?.isFinalized) return;

    setCurrentPrescription((prev) => ({
      ...prev,
      medicines: prev.medicines.map((med) =>
        med.id === medicineId ? { ...med, [field]: value } : med
      ),
    }));
  };

  const handleRemoveMedicine = (medicineId) => {
    if (selectedPrescription?.isFinalized) return;

    setCurrentPrescription((prev) => ({
      ...prev,
      medicines: prev.medicines.filter((med) => med.id !== medicineId),
    }));
  };

  const handlePrescriptionFieldChange = (field, value) => {
    if (selectedPrescription?.isFinalized) return;

    setCurrentPrescription((prev) => ({ ...prev, [field]: value }));
  };

  const handleFinalize = () => {
    if (
      !currentPrescription.diagnosis ||
      currentPrescription.medicines.length === 0
    ) {
      toast.error(
        t("Please add diagnosis and at least one medicine before finalizing"),
      );
      return;
    }

    const finalizedPrescription = {
      ...currentPrescription,
      isFinalized: true,
    };

    setCurrentPrescription(finalizedPrescription);
    setSelectedPrescription(finalizedPrescription);
    toast.success(t("Prescription finalized successfully"));
  };

  const handleSave = () => {
    if (!selectedPrescription?.isFinalized) {
      toast.error(t("Please finalize the prescription before saving"));
      return;
    }

    if (currentPrescription.id) {
      setPrescriptions(
        prescriptions.map((p) =>
          p.id === currentPrescription.id ? currentPrescription : p
        )
      );
    } else {
      const newPrescription = {
        ...currentPrescription,
        id: Date.now(),
      };
      setPrescriptions([...prescriptions, newPrescription]);
      setCurrentPrescription(newPrescription);
      setSelectedPrescription(newPrescription);
    }
    toast.success(t("Prescription saved successfully"));
  };

  const handlePrint = () => {
    if (!selectedPrescription?.isFinalized) {
      toast.error(t("Please finalize the prescription before printing"));
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Prescription - ${
            selectedPrescription.prescriptionNumber
          }</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
            .rx-number { background: #2563eb; color: white; padding: 10px; border-radius: 5px; display: inline-block; margin: 10px 0; }
            .patient-info, .prescription-info { margin: 20px 0; padding: 15px; background: #f8f9fa; border-radius: 8px; }
            .medicines-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .medicines-table th, .medicines-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .medicines-table th { background-color: #2563eb; color: white; }
            .prescription-details { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #ddd; text-align: center; font-size: 12px; color: #666; }
            .signature-section { margin-top: 40px; text-align: right; }
            .signature-line { border-bottom: 1px solid #000; width: 200px; margin: 20px 0 10px auto; }
          </style>
        </head>
        <body>
           <div class="header">
            <h1>${t("SUPERGIT HOSPITAL")}</h1>
            <h2>${t("Medical Prescription")}</h2>
            <div class="rx-number">${t("Rx")}: ${
              selectedPrescription.prescriptionNumber
            }</div>
          </div>
          
          <div class="patient-info">
            <h3>${t("Patient Information")}</h3>
            <p><strong>${t("Name")}:</strong> ${patient?.name || t("N/A")}</p>
            <p><strong>${t("Age")}:</strong> ${patient?.age || t("N/A")}</p>
            <p><strong>${t("Gender")}:</strong> ${patient?.gender || t("N/A")}</p>
            <p><strong>${t("Phone")}:</strong> ${patient?.phone || t("N/A")}</p>
            <p><strong>${t("Address")}:</strong> ${patient?.address || t("N/A")}</p>
          </div>
          
          <div class="prescription-details">
            <div>
              <p><strong>${t("Date")}:</strong> ${selectedPrescription.date}</p>
              <p><strong>${t("Physician")}:</strong> ${
                selectedPrescription.physician
              }</p>
              <p><strong>${t("Diagnosis")}:</strong> ${
                selectedPrescription.diagnosis
              }</p>
            </div>
            <div>
              <p><strong>${t("Pharmacy")}:</strong> ${selectedPrescription.pharmacy}</p>
              <p><strong>${t("Status")}:</strong> ${t(selectedPrescription.status)}</p>
              ${
                selectedPrescription.dateCollected
                  ? `<p><strong>${t("Date Collected")}:</strong> ${selectedPrescription.dateCollected}</p>`
                  : ""
              }
            </div>
          </div>
          
          <h3>${t("Prescribed Medications")}</h3>
          <table class="medicines-table">
            <thead>
              <tr>
                <th>${t("Medicine")}</th>
                <th>${t("Dosage")}</th>
                <th>${t("Route")}</th>
                <th>${t("Frequency")}</th>
                <th>${t("Duration")}</th>
                <th>${t("Purpose")}</th>
                <th>${t("Refills")}</th>
              </tr>
            </thead>
            <tbody>
              ${selectedPrescription.medicines
                .map(
                  (medicine) => `
                <tr>
                  <td>${medicine.name}</td>
                  <td>${medicine.dosage}</td>
                  <td>${medicine.route}</td>
                  <td>${medicine.frequency}</td>
                  <td>${medicine.duration}</td>
                  <td>${medicine.purpose}</td>
                  <td>${medicine.refills}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          ${
            selectedPrescription.comments.length > 0
              ? `
            <div class="prescription-info">
              <h3>${t("Comments & Instructions")}</h3>
              ${selectedPrescription.comments
                .map(
                  (comment) => `
                <p><strong>${comment.date}:</strong> ${comment.text}</p>
              `
                )
                .join("")}
            </div>
          `
              : ""
          }
          
          <div class="signature-section">
            <div class="signature-line"></div>
            <p><strong>${t("Physician Signature")}</strong></p>
            <p>${selectedPrescription.physician}</p>
          </div>
          
          <div class="footer">
            <p>${t("This prescription is valid for 30 days from the date of issue")}</p>
            <p>${t("For any queries, please contact Supergit Hospital")}</p>
            <p>${t("This is a computer generated prescription")}</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAddNewMedicine = () => {
    if (!newMedicine.name || !newMedicine.dosage) {
      toast.error(t("Please fill in at least name and dosage"));
      return;
    }

    const medicineToAdd = {
      id: Date.now(),
      ...newMedicine,
    };

    setMedicines([...medicines, medicineToAdd]);
    setNewMedicine({
      name: "",
      dosage: "",
      route: "",
      presentation: "",
      activeIngredients: "",
      info: "",
      manufacturer: "",
      registrationAuthority: "",
      registrationNumber: "",
      country: "",
    });
    setShowMedicineForm(false);
    toast.success(t("Medicine added successfully"));
  };

  const handleAddComment = () => {
    if (!newComment.trim()) {
      toast.error(t("Please enter a comment"));
      return;
    }

    if (selectedPrescription?.isFinalized) return;

    const comment = {
      id: Date.now(),
      text: newComment,
      date: new Date().toLocaleDateString(),
      files: commentFiles,
    };

    setCurrentPrescription((prev) => ({
      ...prev,
      comments: [...prev.comments, comment],
    }));

    setNewComment("");
    setCommentFiles([]);
    toast.success(t("Comment added successfully"));
  };

  const handleFileUpload = (event, type) => {
    const files = Array.from(event.target.files);

    if (type === "comment") {
      const newFiles = files.map((file) => ({
        id: Date.now() + Math.random(),
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
        type: file.type,
        url: URL.createObjectURL(file),
      }));
      setCommentFiles([...commentFiles, ...newFiles]);
    } else if (type === "prescription") {
      if (selectedPrescription?.isFinalized) return;

      const newFiles = files.map((file) => ({
        id: Date.now() + Math.random(),
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
        type: file.type,
        url: URL.createObjectURL(file),
      }));

      setCurrentPrescription((prev) => ({
        ...prev,
        files: [...prev.files, ...newFiles],
      }));
    }
  };

  const handleDeleteFile = (fileId, type) => {
    if (type === "comment") {
      setCommentFiles(commentFiles.filter((file) => file.id !== fileId));
    } else if (type === "prescription") {
      if (selectedPrescription?.isFinalized) return;

      setCurrentPrescription((prev) => ({
        ...prev,
        files: prev.files.filter((file) => file.id !== fileId),
      }));
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/"))
      return <FaImage className="text-blue-500" />;
    if (fileType === "application/pdf")
      return <FaFilePdf className="text-red-500" />;
    return <FaFile className="text-gray-500" />;
  };

  // Filter functions
  const filteredPrescriptions = prescriptions.filter(
    (prescription) =>
      prescription.prescriptionNumber
        .toLowerCase()
        .includes(prescriptionSearchTerm.toLowerCase()) ||
      prescription.diagnosis
        .toLowerCase()
        .includes(prescriptionSearchTerm.toLowerCase())
  );

  const filteredMedicines = medicines.filter((medicine) =>
    medicine.name.toLowerCase().includes(medicineSearchTerm.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 pb-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">{t("Patient Prescriptions")}</h2>
        </div>

        {/* Prescription Search Bar */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <div
              className="relative cursor-pointer"
              onClick={() => setShowPrescriptionSearch(!showPrescriptionSearch)}
            >
              <input
                ref={prescriptionSearchRef}
                type="text"
                placeholder={t("Search prescriptions...")}
                value={prescriptionSearchTerm}
                onChange={(e) => setPrescriptionSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            {/* Prescription Search Dropdown */}
            {showPrescriptionSearch && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredPrescriptions.length > 0 ? (
                  filteredPrescriptions.map((prescription) => (
                    <div
                      key={prescription.id}
                      onClick={() => handleSelectPrescription(prescription)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">
                        {prescription.prescriptionNumber}
                      </div>
                      <div className="text-sm text-gray-500">
                        {prescription.diagnosis} - {prescription.date}
                      </div>
                      <div className="text-xs text-blue-600">
                        {prescription.isFinalized ? t("Finalized") : t("Draft")}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-gray-500">
                    {t("No prescriptions found")}
                  </div>
                )}
              </div>
            )}
          </div>

          <CustomButton
            onClick={handleNewPrescription}
            icon={<FaPlus size={14} />}
            buttonType="primary"
            className="!h-[48px] !w-[48px] !p-0"
            title={t("New Prescription")}
          />

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <CustomButton
              onClick={handleFinalize}
              label={t("Finalize")}
              icon={<FaLock size={14} />}
              buttonType="warning"
              className="!h-[48px]"
              disabled={selectedPrescription?.isFinalized}
            />
            <CustomButton
              onClick={handleSave}
              label={t("Save")}
              icon={<FaSave size={14} />}
              buttonType="primary"
              className="!h-[48px]"
              disabled={!selectedPrescription?.isFinalized}
            />
            <CustomButton
              onClick={handlePrint}
              label={t("Print")}
              icon={<FaPrint size={14} />}
              buttonType="blue"
              className="!h-[48px]"
              disabled={!selectedPrescription?.isFinalized}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      {selectedPrescription && (
        <div className="flex-1 px-6 pb-6 overflow-y-auto max-h-[calc(100vh-300px)]">
          <div className="space-y-6">
            {/* Medicine Search Bar */}
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <div
                  className="relative cursor-pointer"
                  onClick={() => setShowMedicineSearch(!showMedicineSearch)}
                >
                  <input
                    ref={medicineSearchRef}
                    type="text"
                    placeholder={t("Search medicines...")}
                    value={medicineSearchTerm}
                    onChange={(e) => setMedicineSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={selectedPrescription?.isFinalized}
                  />
                  <FaPills className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>

                {/* Medicine Search Dropdown */}
                {showMedicineSearch && !selectedPrescription?.isFinalized && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredMedicines.length > 0 ? (
                      filteredMedicines.map((medicine) => (
                        <div
                          key={medicine.id}
                          onClick={() => handleAddMedicine(medicine)}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {medicine.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {medicine.dosage} - {medicine.route} -{" "}
                            {medicine.presentation}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-gray-500">
                       {t("No medicines found")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <CustomButton
                onClick={() => setShowMedicineForm(true)}
                icon={<FaPlus size={14} />}
                buttonType="primary"
                className="!h-[48px] !w-[48px] !p-0"
                title={t("Add New Medicine")}
              />
            </div>

            {/* Medicines Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("Prescribed Medicines")}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Name")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Dosage")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Route")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Frequency")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Duration")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Purpose")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Refills")}
                      </th>
                      {!selectedPrescription?.isFinalized && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Action")}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentPrescription.medicines.map((medicine) => (
                      <tr key={medicine.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {medicine.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {medicine.dosage}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {medicine.route}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {selectedPrescription?.isFinalized ? (
                            <span className="text-sm text-gray-900">
                              {medicine.frequency}
                            </span>
                          ) : (
                            <input
                              type="text"
                              value={medicine.frequency}
                              onChange={(e) =>
                                handleMedicineFieldChange(
                                  medicine.id,
                                  "frequency",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder={t("e.g., Twice daily")}
                            />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {selectedPrescription?.isFinalized ? (
                            <span className="text-sm text-gray-900">
                              {medicine.duration}
                            </span>
                          ) : (
                            <input
                              type="text"
                              value={medicine.duration}
                              onChange={(e) =>
                                handleMedicineFieldChange(
                                  medicine.id,
                                  "duration",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder={t("e.g., 7 days")}
                            />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {selectedPrescription?.isFinalized ? (
                            <span className="text-sm text-gray-900">
                              {medicine.purpose}
                            </span>
                          ) : (
                            <input
                              type="text"
                              value={medicine.purpose}
                              onChange={(e) =>
                                handleMedicineFieldChange(
                                  medicine.id,
                                  "purpose",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder={t("Purpose")}
                            />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {selectedPrescription?.isFinalized ? (
                            <span className="text-sm text-gray-900">
                              {medicine.refills}
                            </span>
                          ) : (
                            <input
                              type="text"
                              value={medicine.refills}
                              onChange={(e) =>
                                handleMedicineFieldChange(
                                  medicine.id,
                                  "refills",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              placeholder="0"
                            />
                          )}
                        </td>
                        {!selectedPrescription?.isFinalized && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleRemoveMedicine(medicine.id)}
                              className="text-red-600 hover:text-red-900"
                              title={t("Remove medicine")}
                            >
                              <FaTrash size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {currentPrescription.medicines.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {t(
                      "No medicines added yet. Search and add medicines from above.",
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Side - Prescription Form */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                 {t("Prescription Details")}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Date")}
                    </label>
                    <input
                      type="date"
                      value={currentPrescription.date}
                      onChange={(e) =>
                        handlePrescriptionFieldChange("date", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={selectedPrescription?.isFinalized}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Prescription Number")}
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={currentPrescription.prescriptionNumber}
                        onChange={(e) =>
                          handlePrescriptionFieldChange(
                            "prescriptionNumber",
                            e.target.value
                          )
                        }
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                        disabled={selectedPrescription?.isFinalized}
                      />
                      {!selectedPrescription?.isFinalized && (
                        <button
                          onClick={() =>
                            handlePrescriptionFieldChange(
                              "prescriptionNumber",
                              generatePrescriptionNumber()
                            )
                          }
                          className="p-2 text-gray-600 hover:text-blue-600"
                          title={t("Generate new number")}
                        >
                          <FaRedo size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Diagnosis")}
                    </label>
                    <textarea
                      value={currentPrescription.diagnosis}
                      onChange={(e) =>
                        handlePrescriptionFieldChange(
                          "diagnosis",
                          e.target.value
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      placeholder={t("Enter diagnosis...")}
                      disabled={selectedPrescription?.isFinalized}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Select Physician")}
                    </label>
                    <div className="flex items-center space-x-2">
                      <select
                        value={currentPrescription.physician}
                        onChange={(e) =>
                          handlePrescriptionFieldChange(
                            "physician",
                            e.target.value
                          )
                        }
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={selectedPrescription?.isFinalized}
                      >
                        <option value="">{t("Select Physician")}</option>
                        {physicians.map((physician) => (
                          <option key={physician.id} value={physician.name}>
                            {physician.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="p-2 text-gray-600 hover:text-blue-600"
                        title={t("Add new physician")}
                        onClick={() =>
                          toast.info(t("Add physician feature coming soon"))
                        }
                      >
                        <FaPlus size={14} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Select Pharmacy")}
                    </label>
                    <div className="flex items-center space-x-2">
                      <select
                        value={currentPrescription.pharmacy}
                        onChange={(e) =>
                          handlePrescriptionFieldChange(
                            "pharmacy",
                            e.target.value
                          )
                        }
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={selectedPrescription?.isFinalized}
                      >
                        <option value="">{t("Select Pharmacy")}</option>
                        {pharmacies.map((pharmacy) => (
                          <option key={pharmacy.id} value={pharmacy.name}>
                            {pharmacy.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="p-2 text-gray-600 hover:text-blue-600"
                        title={t("Add new pharmacy")}
                        onClick={() =>
                          toast.info(t("Add pharmacy feature coming soon"))
                        }
                      >
                        <FaPlus size={14} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Status")}
                    </label>
                    <select
                      value={currentPrescription.status}
                      onChange={(e) =>
                        handlePrescriptionFieldChange("status", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={selectedPrescription?.isFinalized}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {t(status)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Date Collected")}
                    </label>
                    <input
                      type="date"
                      value={currentPrescription.dateCollected}
                      onChange={(e) =>
                        handlePrescriptionFieldChange(
                          "dateCollected",
                          e.target.value
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={selectedPrescription?.isFinalized}
                    />
                  </div>
                </div>
              </div>

              {/* Right Side - Tabs */}
              <div className="bg-white border border-gray-200 rounded-lg">
                {/* Tab Headers */}
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-8 px-6" aria-label="Tabs">
                    {[
                      { id: "patient", name: t("Patient"), icon: FaUser },
                      { id: "comments", name: t("Comments"), icon: FaComments },
                      { id: "bill", name: t("Bill"), icon: FaFileInvoice },
                      { id: "files", name: t("Files"), icon: FaFolder },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          activeTab === tab.id
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <tab.icon size={16} />
                        <span>{tab.name}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === "patient" && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {t("Patient Information")}
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">
                            {t("Name")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.name || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            {t("Age")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.age || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            {t("Date of Birth")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.dateOfBirth || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            {t("Phone")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.phone || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            {t("Email")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.email || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">
                            {t("Gender")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.gender || "N/A"}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">
                            {t("Address")}:
                          </span>
                          <span className="ml-2 text-gray-900">
                            {patient?.address || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "comments" && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {t("Comments")}
                      </h4>

                      {!selectedPrescription?.isFinalized && (
                        <div className="space-y-3">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows="3"
                            placeholder={t("Add a comment...")}
                          />

                          <div className="flex items-center space-x-2">
                            <input
                              ref={commentFileInputRef}
                              type="file"
                              multiple
                              onChange={(e) => handleFileUpload(e, "comment")}
                              className="hidden"
                            />
                            <CustomButton
                              onClick={() =>
                                commentFileInputRef.current?.click()
                              }
                              label={t("Attach Files")}
                              icon={<FaUpload size={14} />}
                              buttonType="reset"
                              className="!h-[36px]"
                            />
                            <CustomButton
                              onClick={handleAddComment}
                              label={t("Add Comment")}
                              icon={<FaPlus size={14} />}
                              buttonType="primary"
                              className="!h-[36px]"
                            />
                          </div>

                          {commentFiles.length > 0 && (
                            <div className="space-y-2">
                              <div className="text-sm font-medium text-gray-700">
                                {t("Attached Files")}:
                              </div>
                              {commentFiles.map((file) => (
                                <div
                                  key={file.id}
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                >
                                  <div className="flex items-center space-x-2">
                                    {getFileIcon(file.type)}
                                    <span className="text-sm text-gray-900">
                                      {file.name}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      ({file.size})
                                    </span>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleDeleteFile(file.id, "comment")
                                    }
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="space-y-3">
                        {currentPrescription.comments.map((comment) => (
                          <div
                            key={comment.id}
                            className="p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="text-sm text-gray-900">
                              {comment.text}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {comment.date}
                            </div>
                            {comment.files && comment.files.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {comment.files.map((file) => (
                                  <div
                                    key={file.id}
                                    className="flex items-center space-x-2 text-sm"
                                  >
                                    {getFileIcon(file.type)}
                                    <span
                                      className="text-blue-600 hover:text-blue-800 cursor-pointer"
                                      onClick={() =>
                                        window.open(file.url, "_blank")
                                      }
                                    >
                                      {file.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {currentPrescription.comments.length === 0 && (
                          <div className="text-center py-4 text-gray-500">
                            {t("No comments added yet")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === "bill" && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900">
                       {t("Prescription Bill")}
                      </h4>

                      {/* Bill Content */}
                      <div className="space-y-6">
                        {/* Date Selector */}
                        {!selectedPrescription?.isFinalized && (
                          <div className="flex items-center space-x-2">
                            <FaCalendarAlt className="text-gray-500" />
                            <label className="text-sm font-medium text-gray-700">
                              {t("Date:")}
                            </label>
                            <input
                              type="date"
                              value={
                                currentPrescription.billDate ||
                                new Date().toISOString().split("T")[0]
                              }
                              onChange={(e) =>
                                handlePrescriptionFieldChange(
                                  "billDate",
                                  e.target.value,
                                )
                              }
                              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              disabled={selectedPrescription?.isFinalized}
                            />
                          </div>
                        )}

                        {/* Items Table */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-md font-semibold mb-4">
                            {t("Medicine Items")}
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-3">
                                    {t("Medicine Name")}
                                  </th>
                                  <th className="text-left py-2 px-3">
                                    {t("Quantity")}
                                  </th>
                                  <th className="text-left py-2 px-3">
                                    {t("Price per Item")}
                                  </th>
                                  <th className="text-left py-2 px-3">
                                    {t("Total")}
                                  </th>
                                  {!selectedPrescription?.isFinalized && (
                                    <th className="text-left py-2 px-3">
                                      {t("Action")}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {currentPrescription.medicines.map(
                                  (medicine) => (
                                    <tr
                                      key={medicine.id}
                                      className="border-b border-gray-100"
                                    >
                                      <td className="py-2 px-3">
                                        <span>{medicine.name}</span>
                                      </td>
                                      <td className="py-2 px-3">
                                        {selectedPrescription?.isFinalized ? (
                                          <span>
                                            {medicine.billQuantity || 1}
                                          </span>
                                        ) : (
                                          <input
                                            type="number"
                                            step="any"
                                            value={medicine.billQuantity || 1}
                                            onChange={(e) =>
                                              handleMedicineFieldChange(
                                                medicine.id,
                                                "billQuantity",
                                                parseInt(e.target.value) || 1
                                              )
                                            }
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                            min="1"
                                          />
                                        )}
                                      </td>
                                      <td className="py-2 px-3">
                                        {selectedPrescription?.isFinalized ? (
                                          <span>
                                            {t("Rs.")} {medicine.billPrice || 0}
                                          </span>
                                        ) : (
                                          <input
                                            type="number"
                                            step="any"
                                            value={medicine.billPrice || 0}
                                            onChange={(e) =>
                                              handleMedicineFieldChange(
                                                medicine.id,
                                                "billPrice",
                                                parseFloat(e.target.value) || 0,
                                              )
                                            }
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                            min="0"
                                          />
                                        )}
                                      </td>
                                      <td className="py-2 px-3">
                                        {t("Rs.")}{" "}
                                        {(
                                          (medicine.billQuantity || 1) *
                                          (medicine.billPrice || 0)
                                        ).toFixed(2)}
                                      </td>
                                      {!selectedPrescription?.isFinalized && (
                                        <td className="py-2 px-3">
                                          <span className="text-gray-400 text-sm">
                                            {t("From Rx")}
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>

                          {currentPrescription.medicines.length === 0 && (
                            <div className="text-center py-4 text-gray-500 text-sm">
                              {t("No medicines in prescription to bill")}
                            </div>
                          )}
                        </div>

                        {/* Note */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("Billing Note")}
                          </label>
                          {selectedPrescription?.isFinalized ? (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                              {currentPrescription.billNote || t("No note added")}
                            </div>
                          ) : (
                            <textarea
                              value={currentPrescription.billNote || ""}
                              onChange={(e) =>
                                handlePrescriptionFieldChange(
                                  "billNote",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows="3"
                              placeholder={t("Add a billing note...")}
                            />
                          )}
                        </div>

                        {/* Payment Form (Only shown before finalization) */}
                        {!selectedPrescription?.isFinalized && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-md font-semibold mb-4">
                              {t("Payment Information")}
                            </h4>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("Payment Method")}
                                </label>
                                <select
                                  value={
                                    currentPrescription.paymentMethod || "cash"
                                  }
                                  onChange={(e) =>
                                    handlePrescriptionFieldChange(
                                      "paymentMethod",
                                      e.target.value
                                    )
                                  }
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="cash">{t("Cash")}</option>
                                  <option value="card">{t("Card")}</option>
                                  <option value="online">{t("Online")}</option>
                                  <option value="bank transfer">
                                    {t("Bank Transfer")}
                                  </option>
                                  <option value="mobile money">
                                    {t("Mobile Money")}
                                  </option>
                                  <option value="paypal">{t("PayPal")}</option>
                                  <option value="paystack">{t("Paystack")}</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("Discount Type")}
                                </label>
                                <select
                                  value={
                                    currentPrescription.discountType || "Rs."
                                  }
                                  onChange={(e) =>
                                    handlePrescriptionFieldChange(
                                      "discountType",
                                      e.target.value
                                    )
                                  }
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="Rs.">{t("Rs.")}</option>
                                  <option value="%">{t("%")}</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("Discount Value")}
                                </label>
                                <input
                                  type="number"
                                  step="any"
                                  value={currentPrescription.discountValue || 0}
                                  onChange={(e) =>
                                    handlePrescriptionFieldChange(
                                      "discountValue",
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("Insurance Provider (%)")}
                                </label>
                                <input
                                  type="number"
                                  step="any"
                                  value={
                                    currentPrescription.insuranceProvider || 5
                                  }
                                  onChange={(e) =>
                                    handlePrescriptionFieldChange(
                                      "insuranceProvider",
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  min="0"
                                  max="100"
                                />
                              </div>
                            </div>

                            {/* Bill Totals */}
                            <div className="border-t pt-4">
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span>{t("Subtotal")}:</span>
                                  <span>
                                    Rs.{" "}
                                    {calculatePrescriptionBillTotals().subtotal.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{t("Discount")}:</span>
                                  <span>
                                    {t("Rs.")}{" "}
                                    {calculatePrescriptionBillTotals().discount.toFixed(
                                      2,
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{t("After Discount")}:</span>
                                  <span>
                                    {t("Rs.")}{" "}
                                    {calculatePrescriptionBillTotals().afterDiscount.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span>
                                    {t("Insurance")} (
                                    {currentPrescription.insuranceProvider || 5}
                                    %):
                                  </span>
                                  <span>
                                    {t("Rs.")}{" "}
                                    {calculatePrescriptionBillTotals().insuranceAmount.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between font-bold text-lg border-t pt-2">
                                  <span>{t("Final Amount")}:</span>
                                  <span>
                                    {t("Rs.")}{" "}
                                    {calculatePrescriptionBillTotals().finalAmount.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Payment Details (Only shown after finalization) */}
                        {selectedPrescription?.isFinalized && (
                          <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="text-md font-semibold mb-4">
                              {t("Payment Details")}
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("Payment Method")}
                                </label>
                                <div className="p-2 bg-white border border-gray-200 rounded-md">
                                  {t(currentPrescription.paymentMethod || "cash",)}
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t("Discount")}
                                </label>
                                <div className="p-2 bg-white border border-gray-200 rounded-md">
                                  {currentPrescription.discountValue || 0}{" "}
                                  {t(currentPrescription.discountType || "Rs.")}
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 space-y-2">
                              <div className="flex justify-between">
                                <span>{t("Subtotal")}:</span>
                                <span>
                                  {t("Rs.")}{" "}
                                  {calculatePrescriptionBillTotals().subtotal.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t("Discount")}:</span>
                                <span>
                                  {t("Rs.")}{" "}
                                  {calculatePrescriptionBillTotals().discount.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>{t("After Discount")}:</span>
                                <span>
                                  {t("Rs.")}{" "}
                                  {calculatePrescriptionBillTotals().afterDiscount.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>
                                  {t("Insurance")} (
                                  {currentPrescription.insuranceProvider || 5}
                                  %):
                                </span>
                                <span>
                                  {t("Rs.")}{" "}
                                  {calculatePrescriptionBillTotals().insuranceAmount.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between font-bold text-lg border-t pt-2">
                                <span>{t("Final Amount")}:</span>
                                <span>
                                  {t("Rs.")}{" "}
                                  {calculatePrescriptionBillTotals().finalAmount.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === "files" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {t("Files")}
                        </h4>
                        {!selectedPrescription?.isFinalized && (
                          <CustomButton
                            onClick={() =>
                              prescriptionFileInputRef.current?.click()
                            }
                            label={t("Upload Files")}
                            icon={<FaUpload size={14} />}
                            buttonType="primary"
                            className="!h-[36px]"
                          />
                        )}
                      </div>

                      <input
                        ref={prescriptionFileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => handleFileUpload(e, "prescription")}
                        className="hidden"
                      />

                      <div className="space-y-2">
                        {currentPrescription.files.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                          >
                            <div
                              className="flex items-center space-x-3 cursor-pointer"
                              onClick={() => window.open(file.url, "_blank")}
                            >
                              {getFileIcon(file.type)}
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {file.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {file.size}
                                </div>
                              </div>
                            </div>
                            {!selectedPrescription?.isFinalized && (
                              <button
                                onClick={() =>
                                  handleDeleteFile(file.id, "prescription")
                                }
                                className="text-red-600 hover:text-red-900"
                              >
                                <FaTrash size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                        {currentPrescription.files.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            {t("No files uploaded yet")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedPrescription && (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <FaPills size={64} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">{t("No prescription selected")}</p>
            <p>
              {t("Search for an existing prescription or create a new one")}
            </p>
          </div>
        </div>
      )}

      {/* Medicine Form Modal */}
      {showMedicineForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("Add New Medicine")}
              </h3>
              <button
                onClick={() => setShowMedicineForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Name")} *
                </label>
                <input
                  type="text"
                  value={newMedicine.name}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, name: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("Medicine name")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Dosage")} *
                </label>
                <input
                  type="text"
                  value={newMedicine.dosage}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, dosage: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("e.g., 500mg")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Route")}
                </label>
                <input
                  type="text"
                  value={newMedicine.route}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, route: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("e.g., Oral")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Presentation")}
                </label>
                <input
                  type="text"
                  value={newMedicine.presentation}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      presentation: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("e.g., Tablet")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Active Ingredients")}
                </label>
                <input
                  type="text"
                  value={newMedicine.activeIngredients}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      activeIngredients: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("Active ingredients")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Manufacturer")}
                </label>
                <input
                  type="text"
                  value={newMedicine.manufacturer}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      manufacturer: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("Manufacturer name")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Registration Authority")}
                </label>
                <input
                  type="text"
                  value={newMedicine.registrationAuthority}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      registrationAuthority: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("e.g., FDA")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Registration Number")}
                </label>
                <input
                  type="text"
                  value={newMedicine.registrationNumber}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      registrationNumber: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("Registration number")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Country")}
                </label>
                <input
                  type="text"
                  value={newMedicine.country}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, country: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("Country")}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Info")}
                </label>
                <textarea
                  value={newMedicine.info}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, info: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder={t("Additional information about the medicine")}
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <CustomButton
                onClick={() => setShowMedicineForm(false)}
                label={t("Cancel")}
                buttonType="reset"
                className="!h-[40px]"
              />
              <CustomButton
                onClick={handleAddNewMedicine}
                label={t("Add Medicine")}
                icon={<FaPlus size={14} />}
                buttonType="primary"
                className="!h-[40px]"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionTab;
